import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { METRIC_ID, ModuleKey } from '@cosmos/shared';
import { EntitlementService } from '@cosmos/angular/utils';
import { TranslatePipe } from '@ngx-translate/core';

export type MetricCounterDisplay = 'text' | 'bar';

const WARNING_THRESHOLD = 0.8;

/**
 * Self-contained metric usage counter.
 *
 * Reads the bucket for the given module+metric from EntitlementService and renders:
 * - Nothing (or label + ∞ icon) when the feature has no cap (allowNegative or no bucket).
 * - A monthly counter (label: remaining / limit this month) when resetTarget is set.
 * - A global counter (label: remaining remaining) for prepaid one-time buckets.
 *
 * Translation keys for metric labels follow the convention:
 *   metering.counter.<M>.<K>.parent    for abstract parent metrics (length 2)
 *   metering.counter.<M>.<K>.<D>       for leaf metrics (length 3)
 *
 * Each metric label string should include the availability noun, e.g.:
 *   "Simulations available" / "Simulaciones disponibles"
 */
@Component({
  selector: 'cosmos-metric-counter',
  standalone: true,
  imports: [TranslatePipe],
  template: `
    @if (visible()) {
      <div class="flex flex-col gap-1.5">

        <!-- Label row -->
        <div class="flex items-center gap-2 text-sm">
          <span class="text-surface-500">{{ labelKey() | translate }}:</span>
          @if (isUnlimited()) {
            <i class="fa-solid fa-infinity fa-lg text-primary"></i>
          } @else if (isPrepaid()) {
            <span [class]="valueClass()">
              {{ remaining() }} {{ 'metering.counter.remaining' | translate }}
            </span>
          } @else {
            <span [class]="valueClass()" class="font-bold">
              {{ remaining() }}
              @if (!hasExtraCredit()) {
                <span class="text-surface-500 font-bold"> / {{ limit() }}</span>
                <span class="text-xs text-surface-500 ml-1 font-bold">{{ 'metering.counter.thisMonth' | translate }}</span>
              }
            </span>
          }
        </div>

        <!-- Progress bar (only when display === 'bar' and not unlimited) -->
        @if (display() === 'bar' && !isUnlimited()) {
          <div class="w-full h-1.5 rounded-full bg-surface-200 dark:bg-surface-700 overflow-hidden">
            <div
              class="h-full rounded-full transition-all duration-300"
              [class]="barClass()"
              [style.width.%]="fillPercent()">
            </div>
          </div>
        }

      </div>
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MetricCounterComponent {
  private readonly entitlementService = inject(EntitlementService);

  readonly module = input.required<ModuleKey>();
  readonly metric = input.required<METRIC_ID>();

  /** 'text' shows label + value only. 'bar' adds a progress bar below. */
  readonly display = input<MetricCounterDisplay>('text');

  /**
   * When false, the component renders nothing for unlimited features.
   * When true (default), shows label + ∞ icon for uncapped features.
   */
  readonly showUnlimited = input<boolean>(true);

  // ── Derived state ──────────────────────────────────────────────────────────

  private readonly bucket = computed(() =>
    this.entitlementService.getBucket(this.module(), this.metric())
  );

  readonly isUnlimited = computed(() => {
    const b = this.bucket();
    return !b || b.allowNegative;
  });

  readonly isPrepaid = computed(() => {
    const b = this.bucket();
    return !!b && !b.allowNegative && b.resetTarget === null;
  });

  readonly limit = computed(() => this.bucket()?.resetTarget ?? null);

  /** True when remaining capacity exceeds the monthly reset target (extra prepaid credit on top). */
  readonly hasExtraCredit = computed(() => {
    const b = this.bucket();
    if (!b || b.allowNegative || b.resetTarget === null) return false;
    return b.remaining > b.resetTarget;
  });

  /** Remaining capacity clamped at 0 for display. */
  readonly remaining = computed(() => {
    const b = this.bucket();
    if (!b || b.allowNegative) return 0;
    return Math.max(b.remaining, 0);
  });

  /**
   * Fill percent for the progress bar: remaining / limit, clamped to [0, 100].
   * Bar represents leftover capacity — full = all available, empty = exhausted.
   */
  readonly fillPercent = computed(() => {
    const b = this.bucket();
    if (!b || b.allowNegative || b.resetTarget === null || b.resetTarget === 0) return 0;
    const ratio = Math.max(b.remaining, 0) / b.resetTarget;
    return Math.min(ratio * 100, 100);
  });

  /**
   * Translation key for the metric label.
   * M.K metrics (length 2) get a '.parent' suffix to avoid collision with M.K.D children.
   */
  readonly labelKey = computed(() => {
    const m = this.metric() as string;
    const suffix = m.split('.').length < 3 ? '.parent' : '';
    return `metering.counter.${m}${suffix}`;
  });

  /** Color scale: green/primary when healthy, orange when low, red when exhausted. */
  readonly valueClass = computed(() => {
    const fill = this.fillPercent();
    if (fill <= 0) return 'text-red-500 font-bold';
    if (fill <= (1 - WARNING_THRESHOLD) * 100) return 'text-orange-400 font-bold';
    return 'text-surface-500 font-bold';
  });

  readonly barClass = computed(() => {
    const fill = this.fillPercent();
    if (fill <= 0) return 'bg-red-500';
    if (fill <= (1 - WARNING_THRESHOLD) * 100) return 'bg-orange-400';
    return 'bg-green-600';
  });

  readonly visible = computed(() => {
    if (this.isUnlimited()) return this.showUnlimited();
    return true;
  });
}
